import type { RosterIndicator, RosterSettings } from '../../../../types/scholarships';
import {
  handednessColor,
  paletteColorVar,
  paletteMarkStyle,
  resolveIndicators,
  resolveTrait,
  traitColorKey,
} from './rosterPalette';
import './rosterManagement.css';

/**
 * The three marks a roster board puts on a player wherever they appear: the name in their
 * handedness colour, a pill for their evaluation trait, and a dot per indicator. One component
 * each, so the depth chart, the pitching staff and the lineups cannot drift apart.
 */

interface PlayerNameProps {
  name: string;
  bats: string | null | undefined;
  settings: RosterSettings;
  className?: string;
}

/** A player's name in their handedness colour. Unknown side → ordinary text colour. */
export const HandednessName = ({ name, bats, settings, className }: PlayerNameProps) => (
  <span
    className={['roster-name', className].filter(Boolean).join(' ')}
    style={{ color: handednessColor(bats, settings) }}
  >
    {name}
  </span>
);

interface TraitPillProps {
  traitId: string | null | undefined;
  settings: RosterSettings;
}

/** The player's evaluation trait, or nothing for no trait or one the board has since removed. */
export const TraitPill = ({ traitId, settings }: TraitPillProps) => {
  const trait = resolveTrait(traitId, settings);
  if (!trait) return null;
  return <TraitNamePill name={trait.name} colorKey={traitColorKey(trait.id, settings)} />;
};

/** A trait-name pill tinted with its palette colour, for a legend or a row. */
export const TraitNamePill = ({ name, colorKey }: { name: string; colorKey: string }) => (
  <span
    className="roster-trait-pill"
    style={{ '--roster-pill-color': paletteColorVar(colorKey) } as React.CSSProperties}
  >
    {name}
  </span>
);

export const IndicatorDot = ({ indicator }: { indicator: RosterIndicator }) => (
  <span
    className="roster-indicator-dot"
    style={paletteMarkStyle(indicator.color)}
    title={indicator.name}
    aria-hidden="true"
  />
);

interface IndicatorDotsProps {
  indicatorIds: readonly string[] | null | undefined;
  settings: RosterSettings;
  /**
   * Render the names as screen-reader text. Off where the dots sit inside a control whose
   * aria-label already says them (a depth chart row), since the label replaces the content anyway.
   */
  announce?: boolean;
}

/** One dot per indicator the player carries. Colour is never the only signal: each has a name. */
export const IndicatorDots = ({
  indicatorIds,
  settings,
  announce = true,
}: IndicatorDotsProps) => {
  const indicators = resolveIndicators(indicatorIds, settings);
  if (indicators.length === 0) return null;
  return (
    <span className="roster-indicator-dots">
      {indicators.map((indicator) => (
        <IndicatorDot key={indicator.id} indicator={indicator} />
      ))}
      {announce && (
        <span className="roster-sr-only">
          {indicators.map((i) => i.name).join(', ')}
        </span>
      )}
    </span>
  );
};
